import { useLocalization } from "../../../../i18n/LocalizationProvider";
import MenuText from "../../../../i18n/MenuText";
import DoneIcon from "@mui/icons-material/Done";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    Avatar,
    Box,
    Card,
    CardActionArea,
    Chip,
    Grid,
    TextField,
    Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SortableContainer as BaseSortableContainer, SortableElement } from "react-sortable-hoc";
import config from "../../../../config";
import NestedItemConfigurator from "./NestedItemConfigurator";
import {
    createOptionSelection,
    canSelectCategoryOption,
    getCategoryMaximum,
    categoryMaximumMessage,
    getAddonCode,
    getLookupItem,
    hasItemConfiguration,
    isItemCustomizationEnabled,
} from "./addonTree";

// react-sortable-hoc can intermittently throw when cleaning up the helper element
// (`helper.parentNode` is null). The library assigns handleSortEnd/handleSortCancel
// as *instance fields*, so prototype patching is unreliable. Instead we wrap the
// instance handlers via subclassing. This fix lives in our repo codebase (no
// node_modules patching).
const isRemoveChildNullError = (err) => {
    const msg = String(err?.message || err || "");
    return (
        msg.includes("removeChild") &&
        (msg.includes("null") || msg.includes("parentNode"))
    );
};

const SafeSortableContainer = (WrappedComponent) => {
    const Base = BaseSortableContainer(WrappedComponent);

    class SafeContainer extends Base {
        constructor(...args) {
            super(...args);

            const wrapInstanceHandler = (name) => {
                const orig = this?.[name];
                if (typeof orig !== "function") return;
                this[name] = (...handlerArgs) => {
                    try {
                        // Ensure helper has a parent before library cleanup runs
                        if (
                            this?.helper &&
                            !this.helper.parentNode &&
                            typeof document !== "undefined" &&
                            document.body
                        ) {
                            document.body.appendChild(this.helper);
                        }
                    } catch (e) {
                        // noop
                    }

                    try {
                        return orig.apply(this, handlerArgs);
                    } catch (e) {
                        if (!isRemoveChildNullError(e)) throw e;

                        // Fail closed: prevent app crash. Best-effort cleanup.
                        try {
                            if (this?.helper?.parentNode) {
                                this.helper.parentNode.removeChild(this.helper);
                            }
                        } catch (e2) {
                            // noop
                        }

                        try {
                            if (typeof document !== "undefined" && document.body) {
                                document.body.style.userSelect = "";
                                document.body.style.webkitUserSelect = "";
                            }
                        } catch (e3) {
                            // noop
                        }

                        this.sorting = false;
                        this.helper = null;
                        return undefined;
                    }
                };
            };

            wrapInstanceHandler("handleSortEnd");
            wrapInstanceHandler("handleSortCancel");
        }

        componentWillUnmount() {
            // Dialog close while dragging can cause races; cancel safely.
            try {
                if (this?.sorting && typeof this.handleSortCancel === "function") {
                    this.handleSortCancel();
                }
            } catch (e) {
                // noop
            }
            if (typeof super.componentWillUnmount === "function") {
                super.componentWillUnmount();
            }
        }
    }

    return SafeContainer;
};

// ---- Stable sortable components (must NOT be created inside BundleConfigurator render) ----
const SortableCategoryChip = SortableElement(
    ({ step, isActive, onClick, renderStepLabel }) => (
        <Chip
            label={typeof renderStepLabel === "function" ? renderStepLabel(step) : step?.label}
            clickable
            onClick={onClick}
            color={isActive ? "primary" : "default"}
            variant={isActive ? "filled" : "outlined"}
            sx={{ fontWeight: 700 }}
        />
    )
);

const SortableCategoriesRow = SafeSortableContainer(
    ({ categorySteps, activeIndex, onSelect, renderStepLabel }) => (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {(categorySteps || []).map((s, idx) => (
                <SortableCategoryChip
                    key={s.key}
                    index={idx}
                    step={s}
                    isActive={idx === activeIndex}
                    renderStepLabel={renderStepLabel}
                    onClick={() => onSelect?.(idx)}
                />
            ))}
        </Box>
    )
);

const SortableOptionCard = SortableElement(({ opt, renderOptionNode }) =>
    typeof renderOptionNode === "function" ? renderOptionNode(opt) : null
);

const SortableOptionsGrid = SafeSortableContainer(({ options, renderOptionNode }) => (
    <Grid container spacing={1.25}>
        {(options || []).map((opt, idx) => (
            <SortableOptionCard
                key={`${opt?.item_code || "opt"}_${opt?.label || ""}_${idx}`}
                index={idx}
                opt={opt}
                renderOptionNode={renderOptionNode}
            />
        ))}
    </Grid>
));

const escapeRegExp = (string) => {
    return String(string).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const highlightMatch = (text, query) => {
    const value = String(text ?? "");
    const q = String(query ?? "").trim();
    if (!q) return value;

    try {
        const regex = new RegExp(`(${escapeRegExp(q)})`, "ig");
        const parts = value.split(regex);
        const qLower = q.toLowerCase();
        return parts.map((part, idx) => {
            if (part.toLowerCase() !== qLower)
                return <span key={idx}>{part}</span>;
            return (
                <span
                    key={idx}
                    style={{
                        background: "rgba(255, 235, 59, 0.7)",
                        borderRadius: 4,
                        padding: "0 2px",
                    }}
                >
                    {part}
                </span>
            );
        });
    } catch {
        return value;
    }
};

const safeImg = (pathOrUrl) => {
    if (!pathOrUrl) return null;
    if (String(pathOrUrl).startsWith("http")) return pathOrUrl;
    return `${config.baseUrl}${pathOrUrl}`;
};

const findItemMeta = (lists, itemCode) => {
    if (!itemCode) return null;
    for (const list of lists) {
        if (!Array.isArray(list)) continue;
        const found = list.find((x) => x?.item_code === itemCode);
        if (found) return found;
    }
    return null;
};

const Thumb = ({ src, label, size = 34 }) => {
    const fallback = String(label || "?")
        .trim()
        .slice(0, 2)
        .toUpperCase();

    return (
        <Avatar
            src={src || undefined}
            variant="rounded"
            imgProps={{
                draggable: false,
                onDragStart: (e) => e.preventDefault(),
            }}
            sx={{
                width: size,
                height: size,
                bgcolor: "#f1f3f8",
                color: "#271D1C",
                fontWeight: 700,
                fontSize: 12,
                userSelect: "none",
                WebkitUserDrag: "none",
            }}
        >
            {fallback}
        </Avatar>
    );
};

export default function BundleConfigurator({
    item,
    menuBundle,
    addonItems,
    menuAddons,
    isTabsPos,
    selectedOptions,
    setSelectedOptions,
    selectedAddons,
    setSelectedAddons,
    bundleStep,
    setBundleStep,
    bundleErrors,
    setBundleErrors,
    classes,
    qty,
    reorderMode = false,
    onToggleReorder,
    onReorderCategories,
    onReorderOptions,
    showNotes = true,
    notes,
    setNotes,
    itemLookup,
    showValidation = false,
}) {
    const { t } = useTranslation();
    const { menuText } = useLocalization();
    const categories = useMemo(() => menuBundle?.categories || [], [menuBundle]);
    const [search, setSearch] = useState("");
    const isTouchDevice = useMemo(() => {
        if (typeof window === "undefined") return false;
        return (
            "ontouchstart" in window ||
            (navigator && (navigator.maxTouchPoints || 0) > 0)
        );
    }, []);

    const sortableDragProps = useMemo(() => {
        if (isTouchDevice) {
            return {
                pressDelay: 180,
                pressThreshold: 5,
                distance: 0,
                lockToContainerEdges: true,
            };
        }
        return {
            pressDelay: 0,
            pressThreshold: 0,
            distance: 6,
            lockToContainerEdges: true,
        };
    }, [isTouchDevice]);

    const [isSorting, setIsSorting] = useState(false);
    const setBodyUserSelect = (value) => {
        try {
            if (typeof document === "undefined") return;
            document.body.style.userSelect = value;
            document.body.style.webkitUserSelect = value;
        } catch (e) {}
    };

    const onSortStartCommon = () => {
        setIsSorting(true);
        setBodyUserSelect("none");
    };

    const onSortEndCommon = () => {
        setIsSorting(false);
        setBodyUserSelect("");
    };

    useEffect(() => {
        return () => {
            // Ensure no selection lock remains if Dialog closes mid-drag.
            try {
                if (typeof document !== "undefined" && document.body) {
                    document.body.style.userSelect = "";
                    document.body.style.webkitUserSelect = "";
                }
            } catch (e) {}
        };
    }, []);

    const allowedAddonCodes = Array.isArray(item?.allowed_addons)
        ? item.allowed_addons.filter(Boolean)
        : [];
    const hasExtras = allowedAddonCodes.length > 0;

    const steps = useMemo(() => {
        const s = categories.map((c) => ({
            type: "category",
            key: `cat:${c?.name}`,
            label: menuText(c?.name) || t("bundle.options"),
            cat: c,
        }));
        if (hasExtras) {
            s.push({
                type: "extras",
                key: "extras",
                label: t("bundle.extraAddons"),
            });
        }
        if (showNotes) s.push({ type: "notes", key: "notes", label: t("bundle.notes") });
        return s;
    }, [categories, hasExtras, showNotes, t, menuText]);

    const active = steps[bundleStep] || steps[0];

    useEffect(() => {
        // Reset search whenever the user changes the active category/step
        setSearch("");
    }, [bundleStep, active?.key]);

    const toggleAddon = (code) => {
        if (!code) return;
        setSelectedAddons((prev) =>
            prev?.includes(code)
                ? prev.filter((x) => x !== code)
                : [...(prev || []), code]
        );
    };

    const renderStepLabel = (step) => {
        if (step?.type !== "category") return step?.label;
        const catName = step?.cat?.name;
        const err = catName ? bundleErrors?.[catName] : null;
        if (!err) return step?.label;

        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <span>{step?.label}</span>
                <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: "#d32f2f", fontWeight: 900, lineHeight: 1 }}
                >
                    {err}
                </Typography>
            </Box>
        );
    };

    const renderOptionCardInner = (cat, opt) => {
        const catName = cat?.name;
        const allowMulti = !!cat?.allow_multiple_select;
        const current = selectedOptions?.[catName];
        const isSelected = allowMulti
            ? Array.isArray(current) &&
              current.some((o) => o?.item_code === opt?.item_code)
            : current?.item_code === opt?.item_code;

        const meta =
            getLookupItem(itemLookup, opt?.item_code) ||
            findItemMeta([addonItems, menuAddons], opt?.item_code);
        const img = safeImg(meta?.image) || meta?.cover || null;
        const label = menuText(opt?.label || opt?.item_code || "");
        const price = Number(opt?.added_price) || 0;

        return (
            <Card
                variant="outlined"
                sx={{
                    borderColor: isSelected ? "#1976d2" : "#e5e9f2",
                    borderWidth: isSelected && isTabsPos ? 2 : 1,
                    bgcolor: isSelected ? "rgba(25, 118, 210, 0.08)" : "#fff",
                    boxShadow:
                        isSelected && isTabsPos
                            ? "0px 10px 24px rgba(25, 118, 210, 0.18)"
                            : "none",
                    transform: isSelected && isTabsPos ? "scale(1.01)" : "none",
                    transition:
                        "box-shadow 0.15s ease, transform 0.15s ease, background-color 0.15s ease, border-color 0.15s ease",
                    userSelect: "none",
                    WebkitUserDrag: "none",
                }}
            >
                <CardActionArea
                    disabled={!reorderMode && !canSelectCategoryOption(cat, current, opt)}
                    title={Number.isFinite(getCategoryMaximum(cat)) ? categoryMaximumMessage(cat) : undefined}
                    onClick={
                        reorderMode
                            ? undefined
                            : () =>
                                  setSelectedOptions((prev) => {
                                const next = { ...(prev || {}) };
                                if (allowMulti) {
                                    const list = Array.isArray(next[catName])
                                        ? next[catName]
                                        : [];
                                    if (!canSelectCategoryOption(cat, list, opt)) return prev;
                                    const exists = list.some(
                                        (o) => o?.item_code === opt?.item_code
                                    );
                                    next[catName] = exists
                                        ? list.filter(
                                              (o) =>
                                                  o?.item_code !==
                                                  opt?.item_code
                                          )
                                        : [
                                              ...list,
                                              createOptionSelection(
                                                  opt,
                                                  catName,
                                                  itemLookup,
                                                  {
                                                      visited: new Set([
                                                          item?.item_code,
                                                      ].filter(Boolean)),
                                                  }
                                              ),
                                          ];
                                } else {
                                    next[catName] = isSelected
                                        ? current
                                        : createOptionSelection(
                                              opt,
                                              catName,
                                              itemLookup,
                                              {
                                                  visited: new Set([
                                                      item?.item_code,
                                                  ].filter(Boolean)),
                                              }
                                          );
                                }

                                // If this category previously had a save-time validation error,
                                // clear it as soon as the user makes a valid selection.
                                const mandatory =
                                    cat?.mandatory === undefined
                                        ? true
                                        : !!cat?.mandatory;
                                if (
                                    mandatory &&
                                    catName &&
                                    bundleErrors?.[catName] &&
                                    typeof setBundleErrors === "function"
                                ) {
                                    const nextSelected = next?.[catName];
                                    const hasSelection = allowMulti
                                        ? Array.isArray(nextSelected) &&
                                          nextSelected.length > 0
                                        : !!nextSelected?.item_code;
                                    if (hasSelection) {
                                        setBundleErrors((prevErrs) => {
                                            if (!prevErrs?.[catName])
                                                return prevErrs;
                                            const copy = {
                                                ...(prevErrs || {}),
                                            };
                                            delete copy[catName];
                                            return copy;
                                        });
                                    }
                                }
                                return next;
                            })
                    }
                    sx={{
                        p: isTabsPos ? 1.5 : 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: reorderMode ? "grab" : "pointer",
                        // While sorting, avoid click/selection oddities.
                        pointerEvents: isSorting && reorderMode ? "none" : "auto",
                    }}
                >
                    <Thumb src={img} label={label} size={isTabsPos ? 44 : 34} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant={isTabsPos ? "subtitle2" : "body2"}
                            sx={{
                                fontWeight: isSelected && isTabsPos ? 900 : 800,
                                color:
                                    isSelected && isTabsPos
                                        ? "#1976d2"
                                        : "#271D1C",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize:
                                    isSelected && isTabsPos
                                        ? 15.5
                                        : isTabsPos
                                        ? 14
                                        : undefined,
                            }}
                        >
                            {highlightMatch(label, search)}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color:
                                    isSelected && isTabsPos
                                        ? "rgba(25, 118, 210, 0.85)"
                                        : "#6E7187",
                                fontWeight: isSelected && isTabsPos ? 800 : 700,
                                fontSize:
                                    isSelected && isTabsPos
                                        ? 13
                                        : isTabsPos
                                        ? 12.5
                                        : undefined,
                            }}
                        >
                            +{price}
                        </Typography>
                    </Box>
                    {isSelected ? (
                        <DoneIcon
                            sx={{
                                color: "#1976d2",
                                fontSize: isTabsPos ? 22 : 18,
                            }}
                        />
                    ) : null}
                </CardActionArea>
            </Card>
        );
    };

    const renderOptionCard = (cat, opt) => {
        const catName = cat?.name;
        return (
            <Grid item xs={6} sm={4} md={3} key={`${catName}_${opt?.item_code}`}>
                {renderOptionCardInner(cat, opt)}
            </Grid>
        );
    };

    const extrasStepIdx = categories.length;
    const notesStepIdx = categories.length + (hasExtras ? 1 : 0);

    const filteredCategoryOptions = useMemo(() => {
        if (active?.type !== "category") return [];
        const options = Array.isArray(active?.cat?.options)
            ? active.cat.options
            : [];
        if (reorderMode) return options;
        const q = search.trim().toLowerCase();
        if (!q) return options;
        return options.filter((opt) => {
            const label = String(menuText(opt?.label || "")).toLowerCase();
            const code = String(opt?.item_code || "").toLowerCase();
            return label.includes(q) || code.includes(q);
        });
    }, [active?.type, active?.cat, search, reorderMode, menuText]);

    const activeSelectedOptions = useMemo(() => {
        if (active?.type !== "category") return [];
        const selected = selectedOptions?.[active?.cat?.name];
        return Array.isArray(selected) ? selected : selected ? [selected] : [];
    }, [active?.type, active?.cat?.name, selectedOptions]);

    const updateActiveNestedAddons = (selectedNode, nestedAddons) => {
        const category = active?.cat;
        const categoryName = category?.name;
        if (!categoryName) return;
        setSelectedOptions((previous) => {
            const currentSelection = previous?.[categoryName];
            if (Array.isArray(currentSelection)) {
                return {
                    ...(previous || {}),
                    [categoryName]: currentSelection.map((node) =>
                        node === selectedNode ? { ...node, addons: nestedAddons } : node
                    ),
                };
            }
            return {
                ...(previous || {}),
                [categoryName]:
                    currentSelection === selectedNode
                        ? { ...currentSelection, addons: nestedAddons }
                        : currentSelection,
            };
        });
    };

    const renderAddonCard = (code) => {
        const isSelected = selectedAddons?.includes(code);
        const meta = findItemMeta([addonItems, menuAddons], code) || {};
        const img = safeImg(meta?.image) || meta?.cover || null;
        const price = Number(meta?.standard_rate) || 0;

        return (
            <Grid item xs={6} sm={4} md={3} key={code}>
                <Card
                    variant="outlined"
                    sx={{
                        borderColor: isSelected ? "#2e7d32" : "#e5e9f2",
                        borderWidth: isSelected && isTabsPos ? 2 : 1,
                        bgcolor: isSelected
                            ? "rgba(46, 125, 50, 0.08)"
                            : "#fff",
                        boxShadow:
                            isSelected && isTabsPos
                                ? "0px 10px 24px rgba(46, 125, 50, 0.18)"
                                : "none",
                        transform:
                            isSelected && isTabsPos ? "scale(1.01)" : "none",
                        transition:
                            "box-shadow 0.15s ease, transform 0.15s ease, background-color 0.15s ease, border-color 0.15s ease",
                    }}
                >
                    <CardActionArea
                        onClick={() => toggleAddon(code)}
                        sx={{
                            p: isTabsPos ? 1.5 : 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Thumb
                            src={img}
                            label={menuText(code)}
                            size={isTabsPos ? 44 : 34}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant={isTabsPos ? "subtitle2" : "body2"}
                                sx={{
                                    fontWeight:
                                        isSelected && isTabsPos ? 900 : 800,
                                    color:
                                        isSelected && isTabsPos
                                            ? "#2e7d32"
                                            : "#271D1C",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontSize:
                                        isSelected && isTabsPos
                                            ? 15.5
                                            : isTabsPos
                                            ? 14
                                            : undefined,
                                }}
                            >
                                {<MenuText>{code}</MenuText>}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color:
                                        isSelected && isTabsPos
                                            ? "rgba(46, 125, 50, 0.85)"
                                            : "#6E7187",
                                    fontWeight:
                                        isSelected && isTabsPos ? 800 : 700,
                                    fontSize:
                                        isSelected && isTabsPos
                                            ? 13
                                            : isTabsPos
                                            ? 12.5
                                            : undefined,
                                }}
                            >
                                +{price} {qty > 1 ? `× ${qty}` : ""}
                            </Typography>
                        </Box>
                        {isSelected ? (
                            <DoneIcon
                                sx={{
                                    color: "#2e7d32",
                                    fontSize: isTabsPos ? 22 : 18,
                                }}
                            />
                        ) : null}
                    </CardActionArea>
                </Card>
            </Grid>
        );
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minHeight: 0 }}>
            {/* Jump-to navigation */}
            <Box
                sx={{
                    px: 2,
                    pt: 2,
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                    bgcolor: "#f5f6fa",
                    borderBottom: "1px solid #e5e9f2",
                    pb: 1.25,
                    alignItems: "center",
                }}
            >
                {reorderMode ? (
                    <>
                        <SortableCategoriesRow
                            axis="xy"
                            {...sortableDragProps}
                            helperClass="rm-sortable-helper"
                            hideSortableGhost
                            lockOffset="8%"
                            activeIndex={bundleStep}
                            onSelect={(idx) => setBundleStep(idx)}
                            renderStepLabel={renderStepLabel}
                            categorySteps={categories.map((c) => ({
                                type: "category",
                                key: `cat:${c?.name}`,
                                label: menuText(c?.name) || t("bundle.options"),
                                cat: c,
                            }))}
                            onSortStart={onSortStartCommon}
                            onSortEnd={({ oldIndex, newIndex }) => {
                                onSortEndCommon();
                                onReorderCategories?.({ oldIndex, newIndex });
                            }}
                        />
                        {hasExtras ? (
                            <Chip
                                key="extras"
                                label={t("bundle.extraAddons")}
                                clickable
                                onClick={() => setBundleStep(extrasStepIdx)}
                                color={
                                    bundleStep === extrasStepIdx
                                        ? "primary"
                                        : "default"
                                }
                                variant={
                                    bundleStep === extrasStepIdx
                                        ? "filled"
                                        : "outlined"
                                }
                                sx={{ fontWeight: 700 }}
                            />
                        ) : null}
                        {showNotes && <Chip
                            key="notes"
                            label={t("bundle.notes")}
                            clickable
                            onClick={() => setBundleStep(notesStepIdx)}
                            color={
                                bundleStep === notesStepIdx
                                    ? "primary"
                                    : "default"
                            }
                            variant={
                                bundleStep === notesStepIdx
                                    ? "filled"
                                    : "outlined"
                            }
                            sx={{ fontWeight: 700 }}
                        />}
                    </>
                ) : (
                    steps.map((s, idx) => (
                        <Chip
                            key={s.key}
                            label={renderStepLabel(s)}
                            clickable
                            onClick={() => setBundleStep(idx)}
                            color={idx === bundleStep ? "primary" : "default"}
                            variant={idx === bundleStep ? "filled" : "outlined"}
                            sx={{ fontWeight: 700 }}
                        />
                    ))
                )}

                {/* Reorder toggle (right aligned) */}
                <Chip
                    size="small"
                    label=""
                    title={
                        reorderMode
                            ? t("bundle.doneReordering")
                            : t("bundle.reorderTabsAndOptions")
                    }
                    icon={reorderMode ? <DoneIcon /> : <DragIndicatorIcon />}
                    color={reorderMode ? "success" : "default"}
                    variant={reorderMode ? "filled" : "outlined"}
                    clickable
                    onClick={() => onToggleReorder?.(!reorderMode)}
                    sx={{
                        fontWeight: 900,
                        ml: "auto",
                        minWidth: 34,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "& .MuiChip-icon": {
                            margin: 0,
                        },
                        "& .MuiChip-label": {
                            padding: 0,
                            width: 0,
                        },
                    }}
                />
            </Box>

            {/* Main step content */}
            <Box sx={{ px: 2, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                {active?.type === "category" ? (
                    <>
                        {Number.isFinite(getCategoryMaximum(active.cat)) && <Typography variant="caption">{categoryMaximumMessage(active.cat)}</Typography>}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                py: 1,
                                position: "sticky",
                                top: 56,
                                zIndex: 2,
                                bgcolor: "#f5f6fa",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "#6E7187",
                                    fontWeight: 800,
                                    textTransform: "uppercase",
                                }}
                            >
                                {active.label}
                            </Typography>
                            {!reorderMode && Array.isArray(active?.cat?.options) &&
                            active.cat.options.length > 6 ? (
                                <TextField
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    size="small"
                                    placeholder={t("common.search")}
                                    variant="outlined"
                                    sx={{
                                        minWidth: 220,
                                        "& .MuiOutlinedInput-root": {
                                            bgcolor: "#fff",
                                        },
                                    }}
                                />
                            ) : null}
                        </Box>

                        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pb: 2 }}>
                            {reorderMode ? (
                                <Box sx={{ mt: 1 }}>
                                    <SortableOptionsGrid
                                        axis="xy"
                                        {...sortableDragProps}
                                        helperClass="rm-sortable-helper"
                                        hideSortableGhost
                                        lockOffset="8%"
                                        options={filteredCategoryOptions}
                                        renderOptionNode={(opt) =>
                                            renderOptionCard(active?.cat, opt)
                                        }
                                        onSortStart={onSortStartCommon}
                                        onSortEnd={({ oldIndex, newIndex }) => {
                                            onSortEndCommon();
                                            const categoryName = active?.cat?.name;
                                            if (!categoryName) return;
                                            onReorderOptions?.({
                                                categoryName,
                                                oldIndex,
                                                newIndex,
                                            });
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ mt: 1 }}>
                                    <Grid container spacing={1.25}>
                                        {filteredCategoryOptions.map((opt) =>
                                            renderOptionCard(active.cat, opt)
                                        )}
                                    </Grid>
                                    {activeSelectedOptions.map((selectedNode) => {
                                        const option = (active?.cat?.options || []).find(
                                            (candidate) =>
                                                getAddonCode(candidate) === getAddonCode(selectedNode)
                                        );
                                        const childItem = getLookupItem(
                                            itemLookup,
                                            getAddonCode(selectedNode)
                                        );
                                        if (
                                            !option ||
                                            !isItemCustomizationEnabled(option) ||
                                            !hasItemConfiguration(childItem)
                                        ) {
                                            return null;
                                        }

                                        return (
                                            <Box
                                                key={`customize_${active?.cat?.name}_${getAddonCode(selectedNode)}`}
                                                sx={{
                                                    mt: 1.5,
                                                    p: 1.5,
                                                    border: "1px solid #dce3f0",
                                                    borderRadius: 1.5,
                                                    bgcolor: "#fff",
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 900, mb: 1 }}
                                                >
                                                    {t("bundle.customizeItem", {
                                                        item: menuText(
                                                            selectedNode?.label ||
                                                            selectedNode?.item_name ||
                                                            getAddonCode(selectedNode)),
                                                    })}
                                                </Typography>
                                                <NestedItemConfigurator
                                                    item={childItem}
                                                    value={selectedNode?.addons || []}
                                                    onChange={(nested) =>
                                                        updateActiveNestedAddons(selectedNode, nested)
                                                    }
                                                    itemLookup={itemLookup}
                                                    isTabsPos={isTabsPos}
                                                    showValidation={showValidation}
                                                    visited={new Set(
                                                        [item?.item_code].filter(Boolean)
                                                    )}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    </>
                ) : null}

                {active?.type === "extras" ? (
                    <>
                        <Typography
                            variant="caption"
                            sx={{
                                color: "#6E7187",
                                fontWeight: 800,
                                textTransform: "uppercase",
                            }}
                        >
                            {t("bundle.extraAddons")}
                        </Typography>
                        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pb: 2 }}>
                            <Box sx={{ mt: 1 }}>
                                <Grid container spacing={1.25}>
                                    {allowedAddonCodes.map((code) =>
                                        renderAddonCard(code)
                                    )}
                                </Grid>
                            </Box>
                        </Box>
                    </>
                ) : null}

                {active?.type === "notes" ? (
                    <>
                        <Typography
                            variant="caption"
                            sx={{
                                color: "#6E7187",
                                fontWeight: 800,
                                textTransform: "uppercase",
                                py: 1,
                                position: "sticky",
                                top: 56,
                                zIndex: 2,
                                bgcolor: "#f5f6fa",
                            }}
                        >
                            {t("bundle.notes")}
                        </Typography>
                        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pb: 2 }}>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes?.(e.target.value)}
                                placeholder={t("bundle.addNotesHere")}
                                className={classes?.NotesArea}
                                style={{
                                    minHeight: 180,
                                    maxHeight: 320,
                                }}
                            />
                        </Box>
                    </>
                ) : null}
            </Box>
        </Box>
    );
}
